import { DfmSelectionsNone } from '../../constants/index.js';

/**
 * Extracts the customer's DFM option selections (e.g. corner radii) from a quote's
 * `feedbackResponses` map. Boolean entries are skipped — those are the "Accept change"
 * checkbox acknowledgments of required feedback items, not selections worth displaying.
 * Entries are sorted by title so output is canonical regardless of the order the
 * responses were written — callers compare joined selection strings for equality.
 * @param {{ [key: string]: any } | null | undefined} feedbackResponses
 * @returns {{ title: string, value: string }[]} Answered selections as title/value pairs.
 */
export default function getFeedbackSelections(feedbackResponses) {
  return Object.entries(feedbackResponses || {})
    .filter(([, value]) => typeof value !== 'boolean' && value)
    .map(([title, value]) => ({ title, value: String(value) }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * The canonical single-string form of the customer's DFM selections
 * (e.g. "Corner radii: 50 µm | Draft angle: 2°"), used for part metadata and
 * equality comparisons (e.g. re-order setup-fee checks) — always build the
 * string through here so the formats can never diverge. No selections yields
 * {@link DfmSelectionsNone}, never '' — displays must translate it.
 * @param {{ [key: string]: any } | null | undefined} feedbackResponses
 * @returns {string} Joined selections, or DfmSelectionsNone when there are none.
 */
export function getDfmSelectionsString(feedbackResponses) {
  return getFeedbackSelections(feedbackResponses)
    .map(({ title, value }) => `${title}: ${value}`)
    .join(' | ') || DfmSelectionsNone;
}
